import React from 'react';
import { oneOf, shape, string, arrayOf } from 'prop-types';

import { findString } from 'helpers/findString';

export default useSearchOnItems;

export type TFindItemMethod = (item: any, searchValue: string) => boolean;

/**
 * This hook filters passed items based on the (user input) search value.
 * We often use use sublists inside our lists, such as “Global charts” and “Local charts”,
 * so this hook also filters those if they are present. Presence is determined based on the
 * `children` key.
 *
 * NOTE: it is possible to mix items and sublists.
 *
 * @param items the items to be filtered, potentionally containing sublists.
 * @param searchValue the input to match against the label of an item.
 * @return filtered items based on search value.
 */
function useSearchOnItems(
  items: any[],
  searchValue: string,
  additionalFindMethod?: TFindItemMethod
): any[] {
  const [filteredItems, setFilteredItems] = React.useState(items);

  const searchRegex = React.useCallback(findString(searchValue), [searchValue]);

  React.useEffect(() => {
    if (items.length !== 0) {
      // When the search value is empty (again), reset the state
      // to all items.
      if (!searchValue || searchValue.length === 0) {
        setFilteredItems(items);
      }
      if (items && searchValue && searchValue.length > 0) {
        // As we can mix regular items with sublists, we can't reliably
        // filter only one of those, so we always filter both.
        setFilteredItems(items.map(filterSubItems).filter(filterItems));
      }
    }

    // Match the search value against the label of the item.
    function filterBySearchValue(item: any): boolean {
      return item.label && searchRegex(item.label);
    }

    // Filter the items in the `children` array, if it's there.
    function filterSubItems(item: any): any {
      if ('children' in item) {
        const children = item.children.filter(filterBySearchValue);
        return { ...item, children };
      }
      return item;
    }

    // Filter the items that are not sub lists.
    function filterItems(item: any): boolean {
      if ('children' in item === false) {
        if (additionalFindMethod) {
          return (
            additionalFindMethod(item, searchValue) || filterBySearchValue(item)
          );
        }
        return filterBySearchValue(item);
      }
      return true;
    }
  }, [items, searchRegex, searchValue, additionalFindMethod]);

  return filteredItems;
}

useSearchOnItems.propTypes = {
  items: oneOf([
    shape({
      label: string.isRequired,
      children: arrayOf(shape({ label: string.isRequired })).isRequired,
    }),
    shape({ label: string.isRequired }),
  ]).isRequired,
  searchValue: string.isRequired,
};
